<template>
  <!-- CONTENIDO de la página -->
  <div class="capacitacion-page">
    <!-- Hero -->
    <section
      class="hero-section mb-4 d-flex flex-column align-items-center justify-content-center"
      data-aos="fade-up"
      data-aos-duration="6000"
    >
      <img class="hero-img" :src="bannerHero" alt="Industria 4.0" />

      <!-- Overlay con container para evitar overflow -->
      <div class="hero-overlay d-flex align-items-center">
        <div class="container hero-container">
          <div class="row justify-content-center mb-3 gx-0">
            <div class="col-12 col-lg-10">
              <p class="subtitle fw-medium mb-3">
                {{ translate('subtitle') }}
              </p>
            </div>
          </div>

          <div class="row justify-content-center gx-0">
            <div class="col-auto">
              <h1 class="main-title mb-0 text-center">
                {{ translate('mainTitle') }}
              </h1>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Loader -->
    <div v-if="isLoading" class="loader">
      Cargando cursos...
    </div>

    <!-- Estado vacío -->
    <div v-else-if="cursos.length === 0" class="empty-state">
      No hay cursos disponibles en este momento.
    </div>

    <!-- Grid de cursos -->
    <div v-else class="container courses-grid">
      <div class="row g-4">
        <div
          v-for="curso in pagedCursos"
          :key="curso.id"
          class="col-12 col-sm-6 col-lg-3 d-flex"
          data-aos="zoom-in-up"
          data-aos-duration="1200"
          data-aos-offset="200"
          data-aos-easing="ease-out-cubic"
        >
          <div class="curso-card flex-grow-1 d-flex flex-column shadow-sm">
            <div class="top-bar"></div>
            
            <img
              :src="getFirstImage(curso)"
              class="card-img"
              :alt="'Imagen ' + (curso.nombre[selectedLang] || curso.nombre['es'])"
            />

            <div class="card-body d-flex flex-column p-3">
              <!-- Título primero (negro) -->
              <h5 class="card-title">
                {{
                  curso.nombre[selectedLang] ||
                  curso.nombre['es'] ||
                  translate('courseName')
                }}
              </h5>

              <!-- Nivel justo debajo (azul) -->
              <div class="level-line">
                {{ curso.nivel || translate('level') }}
              </div>

              <!-- Categoría (opcional, más discreta) -->
              <div class="category-line">
                {{
                  (curso.categoria[selectedLang] &&
                    curso.categoria[selectedLang][0]) ||
                  curso.categoria['es'][0] ||
                  translate('category')
                }}
              </div>

              <p class="card-desc">
                {{
                  curso.descripcion[selectedLang] ||
                  curso.descripcion['es'] ||
                  translate('description')
                }}
              </p>

              <router-link
                :to="'/capacitacion/' + curso.slug"
                class="mt-auto ver-curso-btn"
                @click="goToServicio"
              >
                {{ translate('seeCourse') }}
              </router-link>
            </div>

            <div class="bottom-bar"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginación -->
    <nav v-if="!isLoading && totalPages > 1" class="pagination-nav">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: page === 1 }">
          <button class="page-link" @click="goToPage(page - 1)" :disabled="page === 1">&laquo;</button>
        </li>
        <li
          class="page-item"
          v-for="n in totalPages"
          :key="n"
          :class="{ active: n === page }"
        >
          <button class="page-link" @click="goToPage(n)">{{ n }}</button>
        </li>
        <li class="page-item" :class="{ disabled: page === totalPages }">
          <button class="page-link" @click="goToPage(page + 1)" :disabled="page === totalPages">&raquo;</button>
        </li>
      </ul>
    </nav>
  </div>

  <!-- Footer FUERA del wrapper para no heredar estilos -->
  <SiteFooter :selectedLang="selectedLang" />
</template>

<script>
import { useSEO } from '@/composables/useSEO';
import { database, ref as dbRef, get } from '@/firebase.js'
import SiteFooter from '@/components/SiteFooter.vue'
import AOS from 'aos'
import 'aos/dist/aos.css'

const TRANSLATIONS = {
  subtitle: {
    es: 'Aprende a dominar tus procesos con formación especializada, modalidades flexibles, contenidos técnicos, un enfoque práctico y el respaldo de expertos que entienden tu planta desde adentro.',
    en: 'Learn to master your processes with specialized training, flexible formats, technical content, a practical approach, and the support of experts who understand your plant from the inside out.',
    de: 'Lernen Sie, Ihre Prozesse zu beherrschen – mit spezialisierter Schulung, flexiblen Formaten, technischem Inhalt, praxisorientiertem Ansatz und der Unterstützung von Experten, die Ihre Anlage von innen heraus verstehen.'
  },
  mainTitle: { es: 'Zona de capacitación', en: 'Training Zone', de: 'Schulungsbereich' },
  category: { es: 'Categoría', en: 'Category', de: 'Kategorie' },
  level: { es: 'Nivel', en: 'Level', de: 'Niveau' },
  seeCourse: { es: 'Ver curso', en: 'See course', de: 'Kurs ansehen' },
  courseName: { es: 'Nombre del curso', en: 'Course Name', de: 'Kursname' },
  description: { es: 'Descripción del curso', en: 'Course description', de: 'Kursbeschreibung' }
}

export default {
  name: 'CapacitacionPage',
  components: { SiteFooter },
  props: { selectedLang: { type: String, default: 'es' } },
  setup() {
    useSEO({
      title: 'Capacitación Industrial',
      description: 'Cursos especializados en PLC, SCADA, TIA Portal, Node-RED, Grafana e Industria 4.0. Capacitación industrial práctica con expertos de Frenkel Engineering.',
      path: '/capacitacion',
    });
  },
  data () {
    return {
      cursos: [],
      isLoading: true,
      page: 1,
      perPage: 8,
      bannerHero: new URL('@/assets/images/hero_capacitacion.png', import.meta.url).href,
      urlDefault: "https://media.licdn.com/dms/image/v2/C4D12AQHbZlpFTZ0JOQ/article-cover_image-shrink_600_2000/article-cover_image-shrink_600_2000/0/1618508672269?e=2147483647&v=beta&t=J_BbUqPwVDGgiP8PfXix6kNrxGAn7HkdJPJMbzfuqn0"
    }
  },
  computed: {
    totalPages () {
      return Math.ceil(this.cursos.length / this.perPage) || 1
    },
    pagedCursos () {
      const start = (this.page - 1) * this.perPage
      return this.cursos.slice(start, start + this.perPage)
    }
  },
  methods: {
    async fetchCursos () {
      try {
        this.isLoading = true
        
        // Referencia al nodo 'cursos' en Realtime Database
        const cursosRef = dbRef(database, 'cursos')
        
        // Obtener los datos
        const snapshot = await get(cursosRef)
        
        if (snapshot.exists()) {
          const data = snapshot.val()
          
          // Si es un array, usarlo directamente. Si es objeto, convertir a array
          this.cursos = Array.isArray(data)
            ? data.filter(item => item !== null) // Filtrar elementos null
            : Object.keys(data).map(key => ({
                firebaseId: key,
                ...data[key]
              }))
          
          console.log('Cursos cargados:', this.cursos.length)
        } else {
          console.log('No se encontraron cursos')
          this.cursos = []
        }
      } catch (error) {
        console.error('Error al cargar cursos:', error)
        this.cursos = []
      } finally {
        this.isLoading = false
      }
    },
    getFirstImage(curso) {
      var url = "";
      if (curso.urlImagen.includes("https://ejemplo.com/")) {
        url = this.urlDefault
      } else {
        url = curso.urlImagen
      }
      return url
    },
    goToPage (n) {
      if (n >= 1 && n <= this.totalPages) {
        this.page = n
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    
    translate (key) {
      return TRANSLATIONS[key]?.[this.selectedLang] || TRANSLATIONS[key]?.es || key
    },
    
    goToServicio () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },
  
  mounted () {
    // Cargar cursos de Firebase
    this.fetchCursos()
    
    // Inicializar AOS
    AOS.init({ duration: 800, once: true })
    setTimeout(() => AOS.refresh(), 300)
  }
}
</script>

<style scoped>
.capacitacion-page {
  background: #f8fafc;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
  overflow-x: hidden;
}

/* ===============================
   HERO
   =============================== */
.hero-section {
  width: 100%;
  position: relative;
  background: #e0ebf7;
  border-bottom: 1.5px solid #dde;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.hero-img {
  width: 100%;
  height: clamp(180px, 38vw, 380px);
  object-fit: cover;
  object-position: center;
  border-bottom-left-radius: 22px;
  border-bottom-right-radius: 22px;
  box-shadow: 0 6px 28px rgba(20,30,45,.09);
  filter: brightness(.85);
}

.hero-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  padding: 1rem;
  text-align: justify;
  background: linear-gradient(to bottom, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.85) 100%);
}

.hero-container {
  max-width: 1100px;
  padding: 0 1rem;
}

.hero-overlay .subtitle,
.hero-overlay .main-title {
  pointer-events: auto;
}

/* ===============================
   TEXTOS HERO
   =============================== */
.subtitle {
  font-family: 'IBM Plex Mono', monospace;
  font-size: clamp(0.9rem, 0.9rem + 0.3vw, 1.1rem);
  color: #1f1f1f;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 10px 16px;
  margin: 0 auto 12px;
  max-width: min(92vw, 860px);
  text-align: justify;
  overflow-wrap: anywhere;
  hyphens: auto;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.main-title {
  font-family: 'Caveat-Bold', cursive;
  font-size: clamp(1.3rem, 1rem + 2.4vw, 2.5rem);
  font-weight: 700;
  border: 2px solid #aeb2b8;
  border-radius: 13px;
  padding: 0.35em 0.9em;
  background: #fff;
  display: inline-block;
  box-shadow: 0 2px 13px rgba(100,100,120,.08);
  color: #111;
  transition: all 0.3s ease;
}
@media (hover: hover) {
  .main-title:hover {
    background: #2c74b8;
    color: #fff;
    border-color: #2c74b8;
    transform: translateY(-1px);
  }
}

/* Loader y Empty State */
.loader, .empty-state {
  text-align: center;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1.2rem;
  margin: 42px 0 38px;
  color: #888;
}

/* ===============================
   GRID DE CURSOS
   =============================== */
.courses-grid {
  margin-top: 2rem;
  margin-bottom: 36px;
  position: relative;
  z-index: 2;
}

.curso-card {
  background: #fff;
  border-radius: 16px;
  border: 1.7px solid #ced6de;
  min-height: clamp(340px, 280px + 4vw, 420px);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform .18s, box-shadow .18s, background-color .3s;
}
@media (hover: hover) {
  .curso-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 38px rgba(16,46,90,.14);
    border-color: #2c74b8;
  }
}

.card-img {
  width: 100%;
  height: clamp(120px, 18vw, 180px);
  object-fit: cover;
  background: #e7ecf1;
  border-bottom: 2px solid #222;
}
.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 14px;
}

/* TEXTOS EN TARJETAS */
.card-title {
  font-family: 'Caveat-Bold', cursive;
  font-size: clamp(1rem, 0.9rem + 0.5vw, 1.16rem);
  font-weight: 700;
  color: #17213b;
  margin: 6px 0 2px;
}
.level-line {
  color: #1562a6;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .02em;
  margin: 2px 0 8px;
  font-size: .95rem;
}
.category-line {
  font-family: 'IBM Plex Mono', monospace;
  font-size: .86rem;
  color: #666;
  margin-bottom: 8px;
}
.card-desc {
  font-family: 'IBM Plex Mono', monospace;
  color: #31343b;
  font-size: clamp(.72rem, .68rem + .2vw, .86rem);
  margin-bottom: .8em;
  flex: 1;
}
.ver-curso-btn {
  font-family: 'Caveat-Bold', cursive;
  font-weight: 600;
  background: #f4f8fc;
  color: #1562a6;
  border: 1.5px solid #126cbb;
  border-radius: 19px;
  padding: 8px 0;
  margin-top: 3px;
  text-align: center!important;
  text-decoration: none;
  letter-spacing: .02em;
  font-size: clamp(.95rem, .9rem + .2vw, 1.02rem);
  transition: all .3s ease;
}
@media (hover: hover) {
  .ver-curso-btn:hover {
    background: #1562a6;
    color: #fff;
    border-color: #1562a6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,.2);
  }
}
.top-bar, .bottom-bar {
  height: 6px;
  width: 100%;
  background: #161616;
}
.top-bar { border-radius: 14px 14px 0 0; }
.bottom-bar { border-radius: 0 0 13px 13px; margin-top: auto; }

/* PAGINACIÓN */
.pagination-nav {
  margin: 24px 0 30px;
}
.pagination .page-item .page-link {
  font-family: 'IBM Plex Mono', monospace;
  border-radius: 7px;
  color: #1170b0;
  border: 1.4px solid #b2d1e7;
  background: #fff;
  margin: 0 2px;
  padding: 5px 12px;
  font-size: 1rem;
  transition: all .18s;
}
.pagination .page-item.active .page-link,
.pagination .page-item .page-link:hover {
  background: #1182c6;
  color: #fff;
  border-color: #1182c6;
}

/* ===============================
   RESPONSIVE OPTIMIZACIÓN
   =============================== */
@media (max-width: 991.98px) {
  .hero-img { height: clamp(180px, 34vw, 300px); }
}

/* 💡 Una tarjeta por columna en móvil tipo col-12 + contenedor fluido */
@media (max-width: 767.98px) {
  .hero-section {
    flex-direction: column;
    border-bottom: none;
    margin-bottom: 0;
  }

  .hero-img {
    height: 220px;
    border-radius: 0 0 18px 18px;
  }

  .hero-overlay {
    position: relative;
    background: transparent;
    padding: 1.2rem 1rem 0.6rem;
  }

  .subtitle {
    background: transparent;
    box-shadow: none;
    font-size: 0.9rem;
    color: #21242c;
    max-width: 95%;
  }

  .main-title {
    border: none;
    font-size: 1.5rem;
    box-shadow: none;
    background: transparent;
    margin-top: 0.3rem;
  }

  /* 🔹 Contenedor más fluido en móvil */
  .courses-grid.container {
    max-width: 100%;
    padding-left: 12px;
    padding-right: 12px;
  }

  /* 🔹 Grid en una sola columna a ancho completo */
  .courses-grid .row {
    display: flex !important;
    flex-direction: column !important;
    gap: 1.4rem;
  }

  .courses-grid .col-12,
  .courses-grid .col-sm-6,
  .courses-grid .col-lg-3 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .curso-card {
    width: 100%;
    margin: 0 auto;
    border-radius: 14px;
  }

  .card-img {
    height: 200px;
  }

  .ver-curso-btn {
    font-size: 1rem;
  }
}

/* Microajuste */
@media (max-width: 480px) {
  .subtitle { font-size: 0.88rem; padding: 6px 12px; }
  .main-title { font-size: 1.35rem; }
  .card-desc { font-size: 0.82rem; }
}
</style>